import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import GetEntityForSearchDocument from "src/api/graphql/__generated__/documents/GetEntityForSearchDocument";
import SearchDocument from "src/api/graphql/__generated__/documents/SearchDocument";
import parseMaybeUrlForRefCode from "../helpers/parseMaybeUrlForRefCode";
import { SearchContentRow } from "../types";

const useSearchContent = (search: string): { content: SearchContentRow[] | SearchContentRow } => {
	const { refCode } = useMemo(() => parseMaybeUrlForRefCode(search), [search]);

	const { data: { getEntity } = {}, loading: getEntityLoading } = useQuery(
		GetEntityForSearchDocument,
		{
			variables: {
				input: {
					refCode: refCode as string,
				},
			},
			skip: !refCode,
			fetchPolicy: "cache-and-network",
		},
	);

	const {
		data,
		previousData,
		loading: searchLoading,
	} = useQuery(SearchDocument, {
		variables: {
			getCommunitiesInput: {
				limit: 64,
				search,
			},
			getGatheringsInput: {
				limit: 64,
				search,
			},
		},
		fetchPolicy: "cache-and-network",
		skip: !search || !!refCode,
	});

	const content = useMemo<SearchContentRow[] | SearchContentRow>(() => {
		const loading = searchLoading || getEntityLoading;

		const content: SearchContentRow[] = [];
		if (!search) {
			return { variant: "EmptyNoSearch" };
		}
		const communities = [...((data ?? previousData)?.getCommunities?.payload ?? [])];
		const gatherings = [...((data ?? previousData)?.getGatherings?.payload ?? [])];
		if (getEntity?.community) {
			communities.unshift(getEntity.community);
		}
		if (getEntity?.gathering) {
			gatherings.unshift(getEntity.gathering);
		}
		if (communities.length === 0 && gatherings.length === 0) {
			if (loading) {
				return { variant: "Loading" };
			}
			return { variant: "EmptyNoResults" };
		}
		if (communities.length > 0) {
			content.push({ variant: "Header", text: "Groups" });
			content.push({ variant: "Communities", communities });
		}

		if (gatherings.length > 0) {
			content.push({ variant: "Header", text: "Albums " });
			for (const gathering of gatherings) {
				content.push({ variant: "Gathering", gathering });
			}
		}

		return content;
	}, [
		searchLoading,
		getEntityLoading,
		search,
		data,
		previousData,
		getEntity?.community,
		getEntity?.gathering,
	]);

	return {
		content,
	};
};
export default useSearchContent;
